/**
 * Notification settings + Templates page. Renders one of two modes:
 *   scope='platform' — super-admin view, manages platform-wide providers
 *                       and templates seen by all tenants
 *   scope='tenant'   — firm-owner view, manages their own providers +
 *                       templates. Platform templates show as read-only.
 *
 * Three tabs: Providers (creds), Templates, Placeholders reference.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Mail, MessageSquare, Variable, Settings2, Send, Plus, Trash2, RefreshCw, Upload, Clock, ToggleLeft, ToggleRight, X, Search, Globe, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";
import ShareTemplatesEditor from "@/components/ShareTemplatesEditor";
import CommAuditLog from "@/components/CommAuditLog";
import { fmtDateTime } from "@/lib/time";

const ROOT = { platform: "/admin/notifications", tenant: "/tenant/notifications" };

export default function NotificationSettings({ scope = "platform" }) {
    const [tab, setTab] = useState("templates");
    return (
        <>
            <PageHeader title={scope === "platform" ? "Notifications · Platform" : "Notifications · My firm"}
                subtitle={scope === "platform"
                    ? "Manage platform-wide email + WhatsApp providers and the templates shown to every tenant."
                    : "Connect your own SMTP + WhatsApp Business credentials, and build your own templates. If you don't connect a provider, the Law OS platform sends on your behalf using its approved templates."}/>
            <div className="-mx-4 sm:mx-0 mb-6 overflow-x-auto">
                <nav className="flex gap-2 border-b border-border px-4 sm:px-0 min-w-max whitespace-nowrap">
                    {[
                        { id: "templates",    label: "Templates",      Icon: Mail },
                        { id: "share",        label: "Share invoice",  Icon: Mail },
                        { id: "audit",        label: "Audit log",      Icon: Mail },
                        { id: "schedules",    label: "Schedules",      Icon: Clock, adminOnly: true },
                        { id: "providers",    label: "Providers",      Icon: Settings2 },
                        { id: "placeholders", label: "Placeholders",   Icon: Variable },
                    ].filter(t => !t.adminOnly || scope === "platform").map(t => (
                        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`notif-tab-${t.id}`}
                            className={`px-4 py-2.5 text-xs uppercase tracking-[0.16em] border-b-2 -mb-px inline-flex items-center gap-2 shrink-0 ${
                                tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}>
                            <t.Icon className="w-3.5 h-3.5" strokeWidth={1.5}/>{t.label}
                        </button>
                    ))}
                </nav>
            </div>
            {tab === "templates"    && <TemplatesPanel scope={scope}/>}
            {tab === "share"        && <ShareTemplatesEditor scope={scope}/>}
            {tab === "audit"        && <CommAuditLog scope={scope}/>}
            {tab === "schedules"    && scope === "platform" && <SchedulesPanel/>}
            {tab === "providers"    && <ProvidersPanel scope={scope}/>}
            {tab === "placeholders" && <PlaceholdersPanel/>}
        </>
    );
}

// ---------------------------------------------------------------------------
// PROVIDERS PANEL
// ---------------------------------------------------------------------------
function ProvidersPanel({ scope }) {
    const root = ROOT[scope];
    const [rows, setRows] = useState([]);
    const [effective, setEffective] = useState(null);  // tenant-only — null on platform scope
    const [email, setEmail] = useState({ mode: "console", smtp_host: "", smtp_port: 587, smtp_user: "",
                                          smtp_password: "", smtp_insecure_tls: false, api_key: "", from_email: "", from_name: "Law OS" });
    const [whatsapp, setWa] = useState({ waba_id: "", phone_number_id: "", access_token: "", app_secret: "" });
    const [testTo, setTestTo] = useState("");
    const [saving, setSaving] = useState(false);
    // DNS health-check state — null = not run yet; { running, result } otherwise
    const [dns, setDns] = useState({ running: false, result: null });
    // Super-admin-controlled feature flags (e.g. tenant_dns_check_enabled).
    // Tenants see the Check DNS button only if the platform admin enabled it.
    const [tenantFeatures, setTenantFeatures] = useState({ tenant_dns_check_enabled: false });
    const dnsCheckVisible = scope === "platform" || !!tenantFeatures.tenant_dns_check_enabled;

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`${root}/providers`);
            setRows(data);
            const e = data.find(d => d.channel === "email");
            if (e) setEmail({ mode: "console", smtp_port: 587, smtp_user: "", smtp_insecure_tls: false, from_name: "Law OS", api_key: "", ...e.config });
            const w = data.find(d => d.channel === "whatsapp");
            if (w) setWa({ ...whatsapp, ...w.config });
            if (scope === "tenant") {
                try {
                    const { data: eff } = await api.get(`${root}/providers/effective`);
                    setEffective(eff);
                } catch { /* non-critical */ }
            }
            // Feature flags — same endpoint shape on both scopes
            try {
                const { data: flags } = await api.get(`${root}/tenant-features`);
                setTenantFeatures(flags || {});
            } catch { /* non-critical */ }
        } catch (e) { toast.error(apiError(e)); }
    }, [root, scope]);  // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);  // eslint-disable-line

    const toggleTenantFeature = async (key, value) => {
        try {
            const { data } = await api.put(`${root}/tenant-features`, { [key]: value });
            setTenantFeatures(data || {});
            toast.success(`Tenant access ${value ? "enabled" : "disabled"}`);
        } catch (e) { toast.error(apiError(e)); }
    };

    const save = async (channel, config) => {
        setSaving(true);
        try {
            await api.put(`${root}/providers/${channel}`, { config });
            toast.success(`${channel} provider saved — now overriding platform default`);
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const resetToPlatformDefault = async (channel) => {
        setSaving(true);
        try {
            await api.delete(`${root}/providers/${channel}`);
            toast.success(`Switched to platform default for ${channel}`);
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    const testEmail = async () => {
        if (!testTo) { toast.error("Enter a recipient"); return; }
        try {
            const { data } = await api.post(scope === "platform" ? "/admin/notifications/providers/test" : `${root}/providers/test`,
                { channel: "email", to: testTo });
            if (data.ok) {
                toast.success(`Test email sent (mode=${data.mode})`);
            } else {
                // The backend may return a multi-line actionable error for 535 /
                // TLS / DNS issues. Surface it in a wide, long-lived toast so the
                // admin can read every step instead of a vanishing one-liner.
                toast.error(data.error || "Failed", {
                    duration: 30000,
                    style: { whiteSpace: "pre-line", maxWidth: "640px" },
                });
            }
        } catch (e) { toast.error(apiError(e)); }
    };

    // Runs DNS health check on the *currently entered* (not yet saved)
    // values so the admin can iterate without committing bad credentials.
    // Falls back to saved values if the form fields are blank.
    const runDnsCheck = async () => {
        setDns({ running: true, result: null });
        try {
            const body = {
                from_email: email.from_email || null,
                smtp_host: email.mode === "smtp" ? (email.smtp_host || null) : null,
                mode: email.mode || null,
            };
            const { data } = await api.post(`${root}/providers/email/dns-check`, body);
            setDns({ running: false, result: data });
        } catch (e) {
            toast.error(apiError(e));
            setDns({ running: false, result: null });
        }
    };

    return (
        <div className="space-y-6 max-w-3xl">
            {/* EMAIL */}
            <div className="border border-border bg-card p-5" data-testid="notif-provider-email">
                <h3 className="text-sm text-foreground font-semibold mb-1 inline-flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" strokeWidth={1.5}/>Email
                </h3>
                {scope === "tenant" && effective?.email && (
                    <div data-testid="notif-email-source"
                        className={`mb-3 px-3 py-2 border text-[11px] ${
                            effective.email.source === "tenant"
                                ? "bg-amber-500/5 border-amber-500/30 text-amber-200"
                                : "bg-emerald-500/5 border-emerald-500/30 text-emerald-200"
                        }`}>
                        {effective.email.source === "tenant"
                            ? <>Currently using <strong>your firm&apos;s own</strong> email provider — this overrides the platform default for every email your firm sends (notifications, password resets, client invites, etc.).</>
                            : effective.email.source === "platform_default"
                              ? <>Currently using the <strong>Law OS platform default</strong> ({effective.email.mode}). All your firm&apos;s emails ship from there. To use your own SMTP / Resend, fill the fields below and click <em>Save email provider</em>.</>
                              : <>No email provider is configured anywhere yet — outbound emails will be logged to the backend console instead of delivered. Ask the Law OS team to enable a platform default, or configure your own below.</>
                        }
                        {effective.email.source === "tenant" && (
                            <button onClick={() => resetToPlatformDefault("email")} disabled={saving}
                                data-testid="notif-email-use-platform-default"
                                className="ml-2 underline hover:no-underline">
                                Use Law OS platform default instead →
                            </button>
                        )}
                    </div>
                )}
                <p className="text-[10px] text-muted-foreground mb-3">
                    Pick <strong>Resend</strong> (recommended for production — 100/day free), <strong>SMTP</strong> for any other host (Gmail, Outlook, GoDaddy), or <strong>Console</strong> in dev (logs to backend stdout).
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Mode">
                        <select value={email.mode} onChange={e => setEmail({ ...email, mode: e.target.value })}
                            data-testid="notif-email-mode"
                            className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs w-full">
                            <option value="console">Console (dev — logs to backend)</option>
                            <option value="resend">Resend API (recommended for production)</option>
                            <option value="smtp">SMTP (any host — Gmail, Outlook, GoDaddy)</option>
                        </select>
                    </Field>
                    <Field label="From email"><Inp v={email.from_email} on={v => setEmail({ ...email, from_email: v })} tid="notif-email-from"/></Field>
                    <Field label="From name"><Inp v={email.from_name} on={v => setEmail({ ...email, from_name: v })} tid="notif-email-fromname"/></Field>
                    {email.mode === "resend" && (
                        <Field label="Resend API key" hint="Starts with 're_…' · stored encrypted at rest.">
                            <Inp v={email.api_key} on={v => setEmail({ ...email, api_key: v })} type="password" tid="notif-resend-apikey"/>
                        </Field>
                    )}
                    {email.mode === "smtp" && <>
                        <Field label="SMTP host"><Inp v={email.smtp_host} on={v => setEmail({ ...email, smtp_host: v })} tid="notif-smtp-host"/></Field>
                        <Field label="SMTP port"><Inp v={email.smtp_port} on={v => setEmail({ ...email, smtp_port: parseInt(v) || 0 })} tid="notif-smtp-port"/></Field>
                        <Field label="SMTP user"><Inp v={email.smtp_user} on={v => setEmail({ ...email, smtp_user: v })} tid="notif-smtp-user"/></Field>
                        <Field label="SMTP password" hint="Stored encrypted at rest."><Inp v={email.smtp_password} on={v => setEmail({ ...email, smtp_password: v })} type="password" tid="notif-smtp-pass"/></Field>
                        <div className="sm:col-span-2">
                            <label className="flex items-start gap-2 cursor-pointer text-xs text-foreground">
                                <input
                                    type="checkbox"
                                    checked={!!email.smtp_insecure_tls}
                                    onChange={e => setEmail({ ...email, smtp_insecure_tls: e.target.checked })}
                                    data-testid="notif-smtp-insecure-tls"
                                    className="mt-0.5 accent-primary"
                                />
                                <span>
                                    <span className="font-medium">Allow self-signed TLS certificate</span>
                                    <span className="block text-[10px] text-muted-foreground mt-0.5">
                                        Enable this if your mail server uses a self-signed or hostname-mismatched
                                        certificate (common on Webuzo, cPanel, Plesk and other shared-hosting control panels).
                                        Connection is still encrypted; only the server identity is not verified.
                                    </span>
                                </span>
                            </label>
                        </div>
                    </>}
                </div>
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                    <button onClick={() => save("email", email)} disabled={saving} data-testid="notif-save-email"
                        className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 text-[10px] uppercase tracking-[0.16em] font-medium">
                        Save email provider
                    </button>
                    <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="test@example.com"
                        data-testid="notif-test-to"
                        className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs flex-1 max-w-xs"/>
                    <button onClick={testEmail} data-testid="notif-test-email"
                        className="px-3 py-2 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1">
                        <Send className="w-3 h-3" strokeWidth={1.5}/>Send test
                    </button>
                    {dnsCheckVisible && (
                        <button onClick={runDnsCheck} disabled={dns.running} data-testid="notif-email-dns-check"
                            className="px-3 py-2 border border-border text-foreground hover:border-primary hover:text-primary disabled:opacity-30 text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1">
                            <Globe className="w-3 h-3" strokeWidth={1.5}/>{dns.running ? "Checking…" : "Check DNS"}
                        </button>
                    )}
                </div>
                {dnsCheckVisible && dns.result && <DnsHealthPanel result={dns.result} onClose={() => setDns({ running: false, result: null })}/>}

                {/* Super-admin toggle — gates the Check DNS module for tenants */}
                {scope === "platform" && (
                    <div className="mt-5 pt-4 border-t border-border" data-testid="notif-tenant-features">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">
                            Tenant module access
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox"
                                checked={!!tenantFeatures.tenant_dns_check_enabled}
                                onChange={e => toggleTenantFeature("tenant_dns_check_enabled", e.target.checked)}
                                data-testid="notif-toggle-tenant-dns-check"
                                className="mt-0.5 accent-primary"/>
                            <span className="text-xs text-foreground">
                                Allow tenants to run <strong>DNS Health Check</strong>
                                <span className="block text-[10px] text-muted-foreground mt-0.5">
                                    When enabled, firm owners / admins see the same Check DNS button on their own
                                    Providers tab. Leave off to keep the tenant UI minimal and avoid confusion —
                                    you (Law OS team) handle DNS diagnostics for them.
                                </span>
                            </span>
                        </label>
                    </div>
                )}
            </div>

            {/* WHATSAPP */}
            <div className="border border-border bg-card p-5" data-testid="notif-provider-whatsapp">
                <h3 className="text-sm text-foreground font-semibold mb-1 inline-flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-emerald-400" strokeWidth={1.5}/>WhatsApp Business
                </h3>
                {scope === "tenant" && effective?.whatsapp && (
                    <div data-testid="notif-wa-source"
                        className={`mb-3 px-3 py-2 border text-[11px] ${
                            effective.whatsapp.source === "tenant"
                                ? "bg-amber-500/5 border-amber-500/30 text-amber-200"
                                : "bg-emerald-500/5 border-emerald-500/30 text-emerald-200"
                        }`}>
                        {effective.whatsapp.source === "tenant"
                            ? <>Using <strong>your firm&apos;s own WABA</strong>.</>
                            : effective.whatsapp.source === "platform_default"
                              ? <>Using the <strong>Law OS platform WABA</strong> — your firm&apos;s WhatsApp messages ship through Law OS&apos;s approved templates.</>
                              : <>No WhatsApp provider configured anywhere — outbound WA messages will be skipped.</>
                        }
                        {effective.whatsapp.source === "tenant" && (
                            <button onClick={() => resetToPlatformDefault("whatsapp")} disabled={saving}
                                data-testid="notif-wa-use-platform-default"
                                className="ml-2 underline hover:no-underline">
                                Use platform default instead →
                            </button>
                        )}
                    </div>
                )}
                <p className="text-[10px] text-muted-foreground mb-3">
                    Connect a Meta WhatsApp Business Account. Without these, {scope === "tenant" ? "Law OS sends on your behalf using its approved templates" : "WhatsApp messages won't be sent"}. Token is stored AES-encrypted at rest.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="WABA ID"><Inp v={whatsapp.waba_id} on={v => setWa({ ...whatsapp, waba_id: v })} tid="notif-wa-waba"/></Field>
                    <Field label="Phone Number ID"><Inp v={whatsapp.phone_number_id} on={v => setWa({ ...whatsapp, phone_number_id: v })} tid="notif-wa-phone"/></Field>
                    <Field label="System User Access Token" hint="Permanent token from Meta Business Suite."><Inp v={whatsapp.access_token} on={v => setWa({ ...whatsapp, access_token: v })} type="password" tid="notif-wa-token"/></Field>
                    {scope === "platform" && <Field label="App Secret" hint="For webhook HMAC verification (platform only)."><Inp v={whatsapp.app_secret} on={v => setWa({ ...whatsapp, app_secret: v })} type="password" tid="notif-wa-secret"/></Field>}
                </div>
                <button onClick={() => save("whatsapp", whatsapp)} disabled={saving} data-testid="notif-save-whatsapp"
                    className="mt-4 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 text-[10px] uppercase tracking-[0.16em] font-medium">
                    Save WhatsApp provider
                </button>
            </div>

            {/* Existing rows */}
            {rows.length > 0 && (
                <div className="text-[10px] text-muted-foreground">
                    Configured: {rows.map(r => `${r.channel} (${fmtDateTime(r.updated_at)})`).join(" · ")}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// DNS HEALTH PANEL — renders the result of a /providers/email/dns-check call.
// Each check row uses pass/warn/fail/skip colour + an actionable description.
// ---------------------------------------------------------------------------
const DNS_ICONS = {
    pass: { Icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/5", border: "border-emerald-500/30", label: "OK" },
    warn: { Icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/5", border: "border-amber-500/30", label: "Warn" },
    fail: { Icon: XCircle, color: "text-red-400", bg: "bg-red-500/5", border: "border-red-500/30", label: "Fail" },
    skip: { Icon: Globe, color: "text-muted-foreground", bg: "bg-background", border: "border-border", label: "Skip" },
};

function DnsHealthPanel({ result, onClose }) {
    const checks = result?.checks || [];
    const suggestions = result?.suggestions || [];
    const overall = checks.some(c => c.status === "fail")
        ? "fail"
        : checks.some(c => c.status === "warn") ? "warn" : "pass";
    const overallMeta = DNS_ICONS[overall] || DNS_ICONS.skip;

    const copyText = (text, label) => {
        try {
            navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch {
            toast.error("Could not copy — select & copy manually");
        }
    };

    return (
        <div className="mt-4 border border-border bg-background" data-testid="notif-email-dns-result">
            <div className={`flex items-center justify-between px-4 py-3 border-b border-border ${overallMeta.bg}`}>
                <div className="inline-flex items-center gap-2">
                    <overallMeta.Icon className={`w-4 h-4 ${overallMeta.color}`} strokeWidth={1.5}/>
                    <span className="text-sm">
                        DNS health · {result?.domain || "—"}
                        {result?.smtp_host ? ` · ${result.smtp_host}` : ""}
                        {result?.smtp_ip ? ` (${result.smtp_ip})` : ""}
                    </span>
                </div>
                <button onClick={onClose} data-testid="notif-email-dns-close"
                    className="text-muted-foreground hover:text-foreground p-1">
                    <X className="w-4 h-4" strokeWidth={1.5}/>
                </button>
            </div>
            <ul className="divide-y divide-border">
                {checks.map((c, i) => {
                    const meta = DNS_ICONS[c.status] || DNS_ICONS.skip;
                    return (
                        <li key={i} className="px-4 py-3 flex items-start gap-3" data-testid={`notif-email-dns-check-${c.status}`}>
                            <meta.Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.color}`} strokeWidth={1.5}/>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs text-foreground font-medium">{c.name}</span>
                                    <span className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border ${meta.border} ${meta.color}`}>
                                        {meta.label}
                                    </span>
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-1 break-words">{c.detail}</div>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {suggestions.length > 0 && (
                <div className="border-t border-border bg-card" data-testid="notif-email-dns-suggestions">
                    <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">
                            Suggested DNS records · paste at your registrar
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                            ({suggestions.length} record{suggestions.length === 1 ? "" : "s"})
                        </span>
                    </div>
                    <ul className="divide-y divide-border">
                        {suggestions.map((s) => (
                            <DnsSuggestionCard key={s.id} suggestion={s} onCopy={copyText}/>
                        ))}
                    </ul>
                    <div className="px-4 py-3 border-t border-border bg-background text-[10px] text-muted-foreground leading-relaxed">
                        <strong className="text-foreground">How to apply:</strong> log in to where you bought your domain
                        (GoDaddy / Hostinger / Namecheap / your registrar), open <em>DNS Management</em> for the domain,
                        and add each record above using the same <em>Type</em>, <em>Host</em>, and <em>Value</em>. Save and wait
                        5-30 minutes for propagation, then click <em>Check DNS</em> again to verify each gap turns green.
                    </div>
                </div>
            )}

            <div className="px-4 py-2 border-t border-border text-[10px] text-muted-foreground bg-card">
                Checks run live from the Law OS server. SPF / DMARC / DKIM records may take up to 24 hours to propagate after edits at your registrar.
            </div>
        </div>
    );
}

function DnsSuggestionCard({ suggestion, onCopy }) {
    const s = suggestion;
    const isInstructional = s.type === "INSTRUCTIONS" || s.type === "VPS_ACTION" || s.type === "ACTION";

    if (isInstructional) {
        const tagColor = s.type === "VPS_ACTION"
            ? "border-red-500/40 text-red-300"
            : s.type === "ACTION"
                ? "border-amber-500/40 text-amber-300"
                : "border-amber-500/40 text-amber-300";
        return (
            <li className="px-4 py-3" data-testid={`notif-dns-suggestion-${s.id}`}>
                <div className="flex items-start gap-2 mb-1 flex-wrap">
                    <span className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border ${tagColor}`}>
                        {s.id}
                    </span>
                    <span className="text-xs text-foreground font-medium">{s.purpose}</span>
                </div>
                {(s.host || s.value) && (
                    <div className="grid grid-cols-[80px_1fr_auto] gap-x-3 gap-y-1.5 items-center text-[11px] mb-2">
                        {s.host && <>
                            <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px]">
                                {s.type === "VPS_ACTION" ? "Target" : "Host"}
                            </div>
                            <div className="font-mono text-foreground break-all">
                                {s.host}
                                {s.host_hint && <span className="block text-[10px] text-muted-foreground font-sans normal-case tracking-normal">{s.host_hint}</span>}
                            </div>
                            <div></div>
                        </>}
                        {s.value && <>
                            <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px]">
                                {s.type === "VPS_ACTION" && s.value.startsWith("http") ? "URL" : "Value"}
                            </div>
                            <div className="font-mono text-foreground break-all bg-background border border-border px-2 py-1.5">{s.value}</div>
                            <button onClick={() => onCopy(s.value, "Value")}
                                data-testid={`notif-dns-copy-${s.id}-value`}
                                className="text-[9px] uppercase tracking-[0.16em] px-2 py-1 border border-primary text-primary hover:bg-primary/10">
                                Copy
                            </button>
                        </>}
                    </div>
                )}
                <div className="text-[11px] text-muted-foreground mb-2 leading-relaxed">{s.note}</div>
                {s.provider_steps && (
                    <ol className="text-[11px] text-foreground space-y-1 list-decimal list-inside pl-1 mb-2">
                        {s.provider_steps.map((step, i) => <li key={i}>{step}</li>)}
                    </ol>
                )}
                {s.provider_url && (
                    <a href={s.provider_url} target="_blank" rel="noopener noreferrer"
                        data-testid={`notif-dns-suggestion-${s.id}-link`}
                        className="text-[11px] text-primary hover:underline inline-flex items-center gap-1">
                        Open {s.provider_label || "provider"} →
                    </a>
                )}
            </li>
        );
    }
    return (
        <li className="px-4 py-3" data-testid={`notif-dns-suggestion-${s.id}`}>
            <div className="flex items-start gap-2 mb-2 flex-wrap">
                <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-primary/40 text-primary">
                    {s.id}
                </span>
                <span className="text-xs text-foreground font-medium">{s.purpose}</span>
            </div>
            <div className="grid grid-cols-[80px_1fr_auto] gap-x-3 gap-y-1.5 items-center text-[11px]">
                <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px]">Type</div>
                <div className="font-mono text-foreground">{s.type}</div>
                <div></div>

                <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px]">Host</div>
                <div className="font-mono text-foreground break-all">
                    {s.host}
                    {s.host_hint && <span className="block text-[10px] text-muted-foreground font-sans normal-case tracking-normal">{s.host_hint}</span>}
                </div>
                <button onClick={() => onCopy(s.host, "Host")}
                    data-testid={`notif-dns-copy-${s.id}-host`}
                    className="text-[9px] uppercase tracking-[0.16em] px-2 py-1 border border-border hover:border-primary hover:text-primary">
                    Copy
                </button>

                <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px]">Value</div>
                <div className="font-mono text-foreground break-all bg-background border border-border px-2 py-1.5">{s.value}</div>
                <button onClick={() => onCopy(s.value, "Value")}
                    data-testid={`notif-dns-copy-${s.id}-value`}
                    className="text-[9px] uppercase tracking-[0.16em] px-2 py-1 border border-primary text-primary hover:bg-primary/10">
                    Copy
                </button>

                {s.ttl && <>
                    <div className="text-muted-foreground uppercase tracking-[0.12em] text-[9px]">TTL</div>
                    <div className="font-mono text-foreground">{s.ttl}s (or use registrar default)</div>
                    <div></div>
                </>}
            </div>
            {s.note && (
                <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                    <strong className="text-foreground">Note:</strong> {s.note}
                </div>
            )}
        </li>
    );
}

// ---------------------------------------------------------------------------
// TEMPLATES PANEL
// ---------------------------------------------------------------------------
function TemplatesPanel({ scope }) {
    const root = ROOT[scope];
    const [own, setOwn] = useState([]);
    const [platform, setPlatform] = useState([]);
    const [hasOwnWaba, setHasOwnWaba] = useState(true);
    const [active, setActive] = useState(null);   // selected template (own only)
    const [showNew, setShowNew] = useState(false);
    const [placeholders, setPlaceholders] = useState([]);
    const [query, setQuery] = useState("");

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`${root}/templates`);
            if (scope === "platform") {
                setOwn(data); setPlatform([]);
            } else {
                setOwn(data.own || []);
                setPlatform(data.platform || []);
                setHasOwnWaba(!!data.has_own_waba);
            }
        } catch (e) { toast.error(apiError(e)); }
        try {
            const { data } = await api.get("/notifications/placeholders");
            setPlaceholders(data.placeholders || []);
        } catch (e) { /* optional */ }
    }, [root, scope]);

    useEffect(() => { load(); }, [load]);  // eslint-disable-line

    const save = async () => {
        if (!active) return;
        try {
            const { data } = await api.patch(`${root}/templates/${active.id}`, {
                name: active.name, subject: active.subject, body: active.body,
                footer: active.footer, header: active.header,
                language: active.language, category: active.category,
                variables: active.variables || [],
                enabled: active.enabled,
            });
            toast.success("Template saved");
            setActive(data);
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const remove = async () => {
        if (!window.confirm(`Delete template "${active.name}"?`)) return;
        try {
            await api.delete(`${root}/templates/${active.id}`);
            toast.success("Deleted");
            setActive(null); await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const submitMeta = async () => {
        try {
            await api.post(`${root}/templates/${active.id}/submit-to-meta`);
            toast.success("Submitted to Meta for approval");
            await load();
            // refresh active
            const list = scope === "platform" ? own : [...own, ...platform];
            const fresh = list.find(t => t.id === active.id);
            if (fresh) setActive(fresh);
        } catch (e) { toast.error(apiError(e)); }
    };

    const refreshMeta = async () => {
        try {
            await api.post(`${root}/templates/${active.id}/refresh-status`);
            toast.success("Status refreshed");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    /** Clone a read-only platform default into the tenant's own templates. */
    const customizePlatformDefault = async () => {
        if (!active || !active._readOnly) return;
        try {
            const payload = {
                channel: active.channel,
                // unique code so we don't collide with the platform code
                code: `${active.code}_custom_${Date.now().toString(36)}`,
                name: `${active.name} (my copy)`,
                category: active.category || "UTILITY",
                language: active.language || "en_US",
                subject: active.subject || "",
                body: active.body || "",
                variables: active.variables || [],
            };
            const { data } = await api.post(`${root}/templates`, payload);
            toast.success("Copied to your templates — edit freely");
            await load();
            // Switch focus to the new editable copy
            setActive(data);
        } catch (e) { toast.error(apiError(e)); }
    };

    // Case-insensitive filter across name, code, ui_category, channel
    const filterRows = (rows) => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(t => {
            const blob = `${t.name || ""} ${t.code || ""} ${t.ui_category || ""} ${t.channel || ""}`.toLowerCase();
            return blob.includes(q);
        });
    };
    const ownFiltered = useMemo(() => filterRows(own), [own, query]); // eslint-disable-line
    const platformFiltered = useMemo(() => filterRows(platform), [platform, query]); // eslint-disable-line

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
            <div className="space-y-3">
                <button onClick={() => setShowNew(true)} data-testid="notif-template-new"
                    className="w-full inline-flex items-center justify-center gap-1.5 border border-primary text-primary hover:bg-primary/10 px-3 py-2 text-[10px] uppercase tracking-[0.16em] font-medium">
                    <Plus className="w-3 h-3" strokeWidth={2}/>New template
                </button>
                <div className="relative">
                    <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5}/>
                    <input
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Search templates…"
                        data-testid="notif-tpl-search"
                        className="w-full bg-background border border-border focus:border-primary outline-none pl-8 pr-8 py-2 text-xs text-foreground placeholder:text-muted-foreground"
                    />
                    {query && (
                        <button
                            onClick={() => setQuery("")}
                            data-testid="notif-tpl-search-clear"
                            aria-label="Clear search"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                            <X className="w-3.5 h-3.5" strokeWidth={1.5}/>
                        </button>
                    )}
                </div>
                {query && (
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground" data-testid="notif-tpl-search-count">
                        {ownFiltered.length + platformFiltered.length} match{(ownFiltered.length + platformFiltered.length) === 1 ? "" : "es"}
                    </div>
                )}
                <List title={scope === "tenant" ? "My templates" : "Platform templates"}
                    rows={ownFiltered} active={active && !active._readOnly ? active : null} onPick={setActive} tidPrefix="notif-tpl-own"/>
                {scope === "tenant" && platform.length > 0 && (
                    <List title="Platform defaults (read-only)" rows={platformFiltered}
                        active={active && active._readOnly ? active : null}
                        onPick={(t) => setActive({ ...t, _readOnly: true })}
                        tidPrefix="notif-tpl-platform" readOnly/>
                )}
            </div>

            {active ? (
                <div className="space-y-3" data-testid="notif-template-editor">
                    {active._readOnly && (
                        <div className="border border-primary/40 bg-primary/5 px-3 py-2.5 flex items-center justify-between gap-3"
                             data-testid="notif-platform-banner">
                            <div className="text-[11px] text-muted-foreground">
                                <span className="text-primary uppercase tracking-[0.16em] text-[10px] font-medium">Platform default · read-only</span>
                                <span className="ml-2">This is a Law OS-shipped template. Make your own copy to edit.</span>
                            </div>
                            <button onClick={customizePlatformDefault} data-testid="notif-tpl-customize"
                                className="px-3 py-1.5 bg-primary text-primary-foreground hover:bg-primary text-[10px] uppercase tracking-[0.16em] font-medium inline-flex items-center gap-1 whitespace-nowrap">
                                <Plus className="w-3 h-3" strokeWidth={2}/>Customize as my own
                            </button>
                        </div>
                    )}
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                {active.channel} · {active.code} {active.is_system && <span className="text-primary">· system</span>}
                            </div>
                            <input value={active.name} onChange={e => setActive({ ...active, name: e.target.value })}
                                disabled={!!active._readOnly} readOnly={!!active._readOnly}
                                data-testid="notif-tpl-name"
                                className="bg-transparent border-b border-border focus:border-primary outline-none text-lg text-foreground py-1 w-full max-w-md disabled:opacity-80"/>
                        </div>
                        {!active._readOnly && (
                            <div className="flex items-center gap-2">
                            {active.channel === "whatsapp" && (
                                <>
                                    {active.meta_status ? (
                                        <button onClick={refreshMeta} data-testid="notif-tpl-refresh"
                                            className="px-3 py-1.5 border border-border text-muted-foreground hover:text-foreground text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1">
                                            <RefreshCw className="w-3 h-3" strokeWidth={1.5}/>{active.meta_status}
                                        </button>
                                    ) : (
                                        <button onClick={submitMeta} data-testid="notif-tpl-submit"
                                            className="px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1">
                                            <Upload className="w-3 h-3" strokeWidth={1.5}/>Submit to Meta
                                        </button>
                                    )}
                                </>
                            )}
                            {!active.is_system && (
                                <button onClick={remove} data-testid="notif-tpl-delete"
                                    className="px-3 py-1.5 border border-border text-muted-foreground hover:text-rose-400 text-[10px] uppercase tracking-[0.16em] inline-flex items-center gap-1">
                                    <Trash2 className="w-3 h-3" strokeWidth={1.5}/>Delete
                                </button>
                            )}
                            <button onClick={save} data-testid="notif-tpl-save"
                                className="px-4 py-1.5 bg-primary text-primary-foreground hover:bg-primary text-[10px] uppercase tracking-[0.16em] font-medium">
                                Save
                            </button>
                        </div>
                        )}
                    </div>

                    {active.meta_rejected_reason && (
                        <div className="border border-rose-400/40 bg-rose-400/5 p-2 text-[10px] text-rose-300">
                            Rejected by Meta: {active.meta_rejected_reason}
                        </div>
                    )}

                    {active.channel === "email" && (
                        <Field label="Subject">
                            <Inp v={active.subject || ""} on={v => setActive({ ...active, subject: v })} tid="notif-tpl-subject" readOnly={!!active._readOnly}/>
                        </Field>
                    )}

                    {active.channel === "whatsapp" && (
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Language">
                                <Inp v={active.language || "en_US"} on={v => setActive({ ...active, language: v })} tid="notif-tpl-language" readOnly={!!active._readOnly}/>
                            </Field>
                            <Field label="Category">
                                <select value={active.category || "UTILITY"} onChange={e => setActive({ ...active, category: e.target.value })}
                                    disabled={!!active._readOnly}
                                    data-testid="notif-tpl-category"
                                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs w-full disabled:opacity-80">
                                    <option value="UTILITY">UTILITY</option>
                                    <option value="MARKETING">MARKETING</option>
                                    <option value="AUTHENTICATION">AUTHENTICATION</option>
                                </select>
                            </Field>
                        </div>
                    )}

                    <Field label={active.channel === "whatsapp" ? "Body (use {{1}}, {{2}} … in order of variables list)" : "Body"}
                        hint={active.channel === "whatsapp"
                            ? "WhatsApp templates use positional {{1}} {{2}} placeholders. Map them to our variables on the right."
                            : "Use {{first_name}} {{firm_name}} etc — the click-to-insert panel on the right has all available keys."}>
                        <textarea value={active.body || ""} rows={10}
                            onChange={e => setActive({ ...active, body: e.target.value })}
                            readOnly={!!active._readOnly}
                            data-testid="notif-tpl-body"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs font-mono read-only:opacity-80"/>
                    </Field>

                    {active.channel === "whatsapp" && (
                        <>
                            <Field label="Header text (optional)"><Inp v={active.header || ""} on={v => setActive({ ...active, header: v })} tid="notif-tpl-header" readOnly={!!active._readOnly}/></Field>
                            <Field label="Footer text (optional)"><Inp v={active.footer || ""} on={v => setActive({ ...active, footer: v })} tid="notif-tpl-footer" readOnly={!!active._readOnly}/></Field>
                        </>
                    )}

                    {!active._readOnly && (
                        <VariableMapper active={active} setActive={setActive} placeholders={placeholders}/>
                    )}
                </div>
            ) : (
                <div className="border border-border bg-card flex items-center justify-center text-xs text-muted-foreground py-20">
                    Pick a template on the left, or click <strong className="text-foreground">+ New template</strong>.
                </div>
            )}

            {showNew && <NewTemplateModal scope={scope} root={root} onClose={() => setShowNew(false)}
                                            onCreated={async () => { setShowNew(false); await load(); }}
                                            hasOwnWaba={hasOwnWaba}/>}
        </div>
    );
}

function List({ title, rows, active, onPick, tidPrefix, readOnly }) {
    // Group by ui_category for easier scanning
    const grouped = useMemo(() => {
        const m = {};
        rows.forEach(t => { (m[t.ui_category || "Other"] ||= []).push(t); });
        return m;
    }, [rows]);
    return (
        <div className="border border-border bg-card">
            <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{title}</div>
            <div className="max-h-[60vh] overflow-y-auto">
                {rows.length === 0 && <div className="px-3 py-6 text-center text-[10px] text-muted-foreground">No templates yet.</div>}
                {Object.entries(grouped).map(([cat, items]) => (
                    <div key={cat}>
                        <div className="px-3 py-1.5 bg-background border-b border-border text-[9px] uppercase tracking-[0.16em] text-primary">{cat}</div>
                        <div className="divide-y divide-border">
                            {items.map(t => (
                                <button key={t.id} onClick={() => onPick(t)}
                                    data-testid={`${tidPrefix}-${t.id}`}
                                    className={`block w-full text-left px-3 py-2 hover:bg-muted ${active?.id === t.id ? "bg-muted" : ""} ${readOnly ? "opacity-70" : ""}`}>
                                    <div className="text-xs text-foreground flex items-center gap-2">
                                        {t.channel === "email"
                                            ? <Mail className="w-3 h-3 text-primary shrink-0" strokeWidth={1.5}/>
                                            : <MessageSquare className="w-3 h-3 text-emerald-400 shrink-0" strokeWidth={1.5}/>}
                                        <span className="truncate">{t.name}</span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground ml-5">{t.code}</div>
                                    {t.channel === "whatsapp" && t.meta_status && (
                                        <div className={`text-[9px] uppercase tracking-[0.16em] ml-5 mt-0.5 ${
                                            t.meta_status === "APPROVED" ? "text-emerald-400" :
                                            t.meta_status === "REJECTED" ? "text-rose-400" : "text-primary"
                                        }`}>{t.meta_status}</div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function VariableMapper({ active, setActive, placeholders }) {
    const grouped = useMemo(() => {
        const m = {};
        placeholders.forEach(p => { (m[p.group] ||= []).push(p); });
        return m;
    }, [placeholders]);
    const isWa = active.channel === "whatsapp";
    const vars = active.variables || [];

    const togglePlatform = (k) => {
        const next = vars.includes(k) ? vars.filter(v => v !== k) : [...vars, k];
        setActive({ ...active, variables: next });
    };

    const movePos = (idx, delta) => {
        const next = [...vars];
        const j = idx + delta;
        if (j < 0 || j >= next.length) return;
        [next[idx], next[j]] = [next[j], next[idx]];
        setActive({ ...active, variables: next });
    };

    return (
        <div className="border border-border bg-card p-3" data-testid="notif-tpl-variables">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">
                {isWa
                    ? `Body parameters (order = {{1}}, {{2}}, … — currently ${vars.length})`
                    : "Variables used in this template (click to toggle)"}
            </div>
            {isWa && vars.length > 0 && (
                <ol className="space-y-1 mb-3">
                    {vars.map((v, idx) => (
                        <li key={v} className="flex items-center gap-2 text-xs">
                            <span className="text-muted-foreground w-8">{`{{${idx + 1}}}`}</span>
                            <span className="text-foreground">{v}</span>
                            <button onClick={() => movePos(idx, -1)} className="text-muted-foreground hover:text-foreground">↑</button>
                            <button onClick={() => movePos(idx, 1)} className="text-muted-foreground hover:text-foreground">↓</button>
                            <button onClick={() => togglePlatform(v)} className="text-rose-400 hover:text-rose-300 ml-auto"><Trash2 className="w-3 h-3"/></button>
                        </li>
                    ))}
                </ol>
            )}
            <div className="space-y-3">
                {Object.entries(grouped).map(([group, items]) => (
                    <div key={group}>
                        <div className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{group}</div>
                        <div className="flex flex-wrap gap-1.5">
                            {items.map(p => {
                                const used = vars.includes(p.key);
                                return (
                                    <button key={p.key} onClick={() => togglePlatform(p.key)}
                                        data-testid={`notif-var-${p.key}`}
                                        title={`Sample: ${p.sample}`}
                                        className={`text-[10px] px-2 py-1 border ${
                                            used ? "border-primary text-primary bg-primary/10" :
                                            "border-border text-muted-foreground hover:text-foreground"
                                        }`}>
                                        {`{{${p.key}}}`}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function NewTemplateModal({ scope, root, onClose, onCreated, hasOwnWaba }) {
    const [form, setForm] = useState({
        channel: "email", code: "", name: "",
        category: "UTILITY", language: "en_US",
        subject: "", body: "", variables: [],
    });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!form.code || !form.name || !form.body) { toast.error("Code, name, body are required"); return; }
        if (form.channel === "email" && !form.subject) { toast.error("Subject required for email"); return; }
        if (scope === "tenant" && form.channel === "whatsapp" && !hasOwnWaba) {
            toast.error("Connect your own WhatsApp Business credentials first."); return;
        }
        setSaving(true);
        try {
            await api.post(`${root}/templates`, form);
            toast.success("Template created");
            onCreated();
        } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()} className="bg-card border border-border max-w-lg w-full p-5" data-testid="notif-new-template-modal">
                <h3 className="text-lg text-foreground font-semibold mb-3">New template</h3>
                <div className="space-y-3">
                    <Field label="Channel">
                        <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}
                            data-testid="notif-new-channel"
                            className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs w-full">
                            <option value="email">Email</option>
                            <option value="whatsapp">WhatsApp</option>
                        </select>
                    </Field>
                    <Field label="Code (slug — used as the WhatsApp template name)">
                        <Inp v={form.code} on={v => setForm({ ...form, code: v.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} tid="notif-new-code"/>
                    </Field>
                    <Field label="Display name"><Inp v={form.name} on={v => setForm({ ...form, name: v })} tid="notif-new-name"/></Field>
                    {form.channel === "email" && <Field label="Subject"><Inp v={form.subject} on={v => setForm({ ...form, subject: v })} tid="notif-new-subject"/></Field>}
                    <Field label="Body">
                        <textarea value={form.body} rows={6}
                            onChange={e => setForm({ ...form, body: e.target.value })}
                            data-testid="notif-new-body"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs font-mono"/>
                    </Field>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Cancel</button>
                    <button onClick={submit} disabled={saving} data-testid="notif-new-create"
                        className="px-4 py-1.5 bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 text-[10px] uppercase tracking-[0.16em] font-medium">
                        {saving ? "Creating…" : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// PLACEHOLDERS PANEL
// ---------------------------------------------------------------------------
function PlaceholdersPanel() {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        api.get("/notifications/placeholders").then(r => setRows(r.data.placeholders || []))
           .catch(e => toast.error(apiError(e)));
    }, []);
    const grouped = useMemo(() => {
        const m = {};
        rows.forEach(p => { (m[p.group] ||= []).push(p); });
        return m;
    }, [rows]);
    return (
        <div className="space-y-6 max-w-3xl" data-testid="notif-placeholders-panel">
            <p className="text-xs text-muted-foreground">Use these inside any email body / subject / WhatsApp template body. They are auto-filled when the message is sent.</p>
            {Object.entries(grouped).map(([group, items]) => (
                <div key={group}>
                    <h4 className="text-xs uppercase tracking-[0.16em] text-primary mb-2">{group}</h4>
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                            <tr className="border-b border-border">
                                <th className="text-left py-1.5">Placeholder</th>
                                <th className="text-left py-1.5">What it is</th>
                                <th className="text-left py-1.5">Sample</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(p => (
                                <tr key={p.key} className="border-b border-card">
                                    <td className="py-1.5"><code className="bg-background px-1.5 py-0.5 text-primary">{`{{${p.key}}}`}</code></td>
                                    <td className="py-1.5 text-muted-foreground">{p.label}</td>
                                    <td className="py-1.5 text-muted-foreground">{p.sample}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// shared UI bits
// ---------------------------------------------------------------------------
function Field({ label, hint, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{label}</div>
            {children}
            {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
        </label>
    );
}
function Inp({ v, on, type = "text", tid, readOnly }) {
    return <input type={type} value={v ?? ""} onChange={e => on(e.target.value)} data-testid={tid}
        readOnly={!!readOnly}
        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs read-only:opacity-80"/>;
}

// ---------------------------------------------------------------------------
// SCHEDULES PANEL — Rules + upcoming/sent queue (super admin only)
// ---------------------------------------------------------------------------
function SchedulesPanel() {
    const [sub, setSub] = useState("rules");
    return (
        <div>
            <div className="flex gap-2 mb-4 text-[10px] uppercase tracking-[0.16em]">
                <button onClick={() => setSub("rules")} data-testid="notif-sched-sub-rules"
                    className={`px-3 py-1.5 border ${sub === "rules" ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                    Rules
                </button>
                <button onClick={() => setSub("queue")} data-testid="notif-sched-sub-queue"
                    className={`px-3 py-1.5 border ${sub === "queue" ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                    Queue
                </button>
            </div>
            {sub === "rules" ? <RulesList/> : <QueueList/>}
        </div>
    );
}

function RulesList() {
    const [rows, setRows] = useState([]);
    const [showNew, setShowNew] = useState(false);
    const load = useCallback(async () => {
        try { const { data } = await api.get("/admin/notifications/schedule-rules"); setRows(data); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);  // eslint-disable-line

    const toggle = async (r) => {
        try { await api.patch(`/admin/notifications/schedule-rules/${r.id}`, { enabled: !r.enabled }); await load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const updateDelay = async (r, h) => {
        try { await api.patch(`/admin/notifications/schedule-rules/${r.id}`, { delay_hours: parseFloat(h) }); toast.success("Delay updated"); await load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const remove = async (r) => {
        if (!window.confirm(`Delete rule "${r.name}"?`)) return;
        try { await api.delete(`/admin/notifications/schedule-rules/${r.id}`); await load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const rulesCtrl = useTableControls(rows, {
        accessors: { enabled: (r) => r.enabled ? "on" : "off" },
    });

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">When an event fires, every enabled rule queues a templated message at <strong>now + delay</strong>. Negative delays mean &ldquo;before&rdquo; — used for &ldquo;remind 24h before hearing&rdquo; style rules.</p>
                <button onClick={() => setShowNew(true)} data-testid="notif-sched-new"
                    className="inline-flex items-center gap-1.5 border border-primary text-primary hover:bg-primary/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] font-medium">
                    <Plus className="w-3 h-3" strokeWidth={2}/>New rule
                </button>
            </div>
            <FilterChip ctrl={rulesCtrl} className="mb-2"/>
            <div className="border border-border bg-card" data-testid="notif-sched-rules">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <tr className="border-b border-border">
                            <SortableTh field="name" ctrl={rulesCtrl} className="text-left px-4 py-2">Rule</SortableTh>
                            <SortableTh field="event" ctrl={rulesCtrl} className="text-left px-4 py-2">Event</SortableTh>
                            <SortableTh field="template_code" ctrl={rulesCtrl} className="text-left px-4 py-2">Template</SortableTh>
                            <SortableTh field="delay_hours" ctrl={rulesCtrl} className="text-right px-4 py-2" align="right">Delay (h)</SortableTh>
                            <SortableTh field="enabled" ctrl={rulesCtrl} className="text-center px-4 py-2" align="center">Enabled</SortableTh>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rulesCtrl.rows.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-6">No rules yet.</td></tr>}
                        {rulesCtrl.rows.map(r => (
                            <tr key={r.id} className="border-b border-card" data-testid={`notif-rule-${r.id}`}>
                                <td className="px-4 py-2.5">
                                    <div className="text-foreground">{r.name}</div>
                                    <div className="text-[10px] text-muted-foreground">{r.category}</div>
                                </td>
                                <td className="px-4 py-2.5"><code className="text-[10px] bg-background px-1.5 py-0.5 text-primary">{r.event}</code></td>
                                <td className="px-4 py-2.5 text-muted-foreground">{r.template_code}</td>
                                <td className="px-4 py-2.5 text-right">
                                    <input type="number" defaultValue={r.delay_hours} step={1}
                                        onBlur={e => { if (parseFloat(e.target.value) !== r.delay_hours) updateDelay(r, e.target.value); }}
                                        data-testid={`notif-rule-delay-${r.id}`}
                                        className="w-20 bg-background border border-border focus:border-primary outline-none px-2 py-1 text-right text-xs"/>
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                    <button onClick={() => toggle(r)} data-testid={`notif-rule-toggle-${r.id}`}>
                                        {r.enabled
                                            ? <ToggleRight className="w-5 h-5 text-primary inline" strokeWidth={1.5}/>
                                            : <ToggleLeft className="w-5 h-5 text-muted-foreground inline" strokeWidth={1.5}/>}
                                    </button>
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                    {!r.is_system && (
                                        <button onClick={() => remove(r)} data-testid={`notif-rule-delete-${r.id}`}
                                            className="text-muted-foreground hover:text-rose-400">
                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5}/>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {showNew && <NewRuleModal onClose={() => setShowNew(false)} onCreated={async () => { setShowNew(false); await load(); }}/>}
        </div>
    );
}

function NewRuleModal({ onClose, onCreated }) {
    const [form, setForm] = useState({ event: "", template_code: "", name: "", delay_hours: 0, category: "Other", enabled: true });
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (!form.event || !form.template_code || !form.name) { toast.error("All fields required"); return; }
        setSaving(true);
        try { await api.post("/admin/notifications/schedule-rules", form); toast.success("Rule created"); onCreated(); }
        catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()} className="bg-card border border-border max-w-md w-full p-5" data-testid="notif-sched-new-modal">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg text-foreground font-semibold">New schedule rule</h3>
                    <button onClick={onClose}><X className="w-4 h-4 text-muted-foreground"/></button>
                </div>
                <div className="space-y-3">
                    <Field label="Event code" hint="Examples: user.registered, invoice.created, hearing.scheduled, task.created">
                        <Inp v={form.event} on={v => setForm({ ...form, event: v })} tid="notif-sched-event"/>
                    </Field>
                    <Field label="Template code" hint="Must match an existing template's code (any channel).">
                        <Inp v={form.template_code} on={v => setForm({ ...form, template_code: v })} tid="notif-sched-template"/>
                    </Field>
                    <Field label="Display name"><Inp v={form.name} on={v => setForm({ ...form, name: v })} tid="notif-sched-name"/></Field>
                    <Field label="Delay (hours)" hint="0 = immediate. 72 = 3 days after event. -24 = 24h before event (when event_time is anchored).">
                        <Inp v={form.delay_hours} on={v => setForm({ ...form, delay_hours: parseFloat(v) || 0 })} tid="notif-sched-delay"/>
                    </Field>
                    <Field label="Category"><Inp v={form.category} on={v => setForm({ ...form, category: v })} tid="notif-sched-category"/></Field>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Cancel</button>
                    <button onClick={submit} disabled={saving} data-testid="notif-sched-new-create"
                        className="px-4 py-1.5 bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 text-[10px] uppercase tracking-[0.16em] font-medium">
                        {saving ? "Creating…" : "Create rule"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function QueueList() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("pending");
    const load = useCallback(async () => {
        try {
            const q = filter === "all" ? "" : `?status=${filter}`;
            const { data } = await api.get(`/admin/notifications/scheduled${q}`);
            setRows(data);
        } catch (e) { toast.error(apiError(e)); }
    }, [filter]);
    useEffect(() => { load(); }, [load]);  // eslint-disable-line

    const cancel = async (m) => {
        if (!window.confirm(`Cancel this pending message (${m.template_code})?`)) return;
        try { await api.post(`/admin/notifications/scheduled/${m.id}/cancel`); toast.success("Cancelled"); await load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const queueCtrl = useTableControls(rows);

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em]">
                {["pending", "sent", "failed", "cancelled", "all"].map(s => (
                    <button key={s} onClick={() => setFilter(s)} data-testid={`notif-queue-filter-${s}`}
                        className={`px-3 py-1.5 border ${filter === s ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {s}
                    </button>
                ))}
                <button onClick={load} className="ml-auto text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" strokeWidth={1.5}/>Refresh
                </button>
            </div>
            <FilterChip ctrl={queueCtrl} className="mb-2"/>
            <div className="border border-border bg-card overflow-x-auto" data-testid="notif-sched-queue">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <tr className="border-b border-border">
                            <SortableTh field="scheduled_for" ctrl={queueCtrl} className="text-left px-4 py-2">Scheduled for</SortableTh>
                            <SortableTh field="event" ctrl={queueCtrl} className="text-left px-4 py-2">Event</SortableTh>
                            <SortableTh field="template_code" ctrl={queueCtrl} className="text-left px-4 py-2">Template</SortableTh>
                            <SortableTh field="status" ctrl={queueCtrl} className="text-left px-4 py-2">Status</SortableTh>
                            <SortableTh field="attempts" ctrl={queueCtrl} className="text-center px-4 py-2" align="center">Attempts</SortableTh>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {queueCtrl.rows.length === 0 && <tr><td colSpan={6} className="text-center text-muted-foreground py-6">No messages.</td></tr>}
                        {queueCtrl.rows.map(m => (
                            <tr key={m.id} className="border-b border-card" data-testid={`notif-queue-row-${m.id}`}>
                                <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{fmtDateTime(m.scheduled_for)}</td>
                                <td className="px-4 py-2"><code className="text-[10px] bg-background px-1.5 py-0.5 text-primary">{m.event}</code></td>
                                <td className="px-4 py-2 text-muted-foreground">{m.template_code}</td>
                                <td className="px-4 py-2">
                                    <span className={`text-[10px] uppercase tracking-[0.16em] ${
                                        m.status === "sent"      ? "text-emerald-400" :
                                        m.status === "pending"   ? "text-primary" :
                                        m.status === "failed"    ? "text-rose-400" :
                                        m.status === "cancelled" ? "text-muted-foreground" : "text-foreground"
                                    }`}>{m.status}</span>
                                </td>
                                <td className="px-4 py-2 text-center text-muted-foreground">{m.attempts}</td>
                                <td className="px-4 py-2 text-right">
                                    {m.status === "pending" && (
                                        <button onClick={() => cancel(m)} data-testid={`notif-queue-cancel-${m.id}`}
                                            className="text-muted-foreground hover:text-rose-400 text-[10px] uppercase tracking-[0.16em]">
                                            Cancel
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
